import { type BookmarksIdeaPriorityListAttributes } from './generated/BookmarksIdeaPriorityListAttributes';
import ApplicationModel, { attr } from '../ApplicationModel';
import { model } from '../modelBuilder';
import Idea from './Idea';
import Feature from './Feature';
import Epic from './Epic';
import Initiative from './Initiative';
import Release from './Release';
import {
  HasBookmarksPriorityList,
  projectWorkflowScope,
  SHARED_BOOKMARK_COLUMNS,
  SHARED_BOOKMARK_MERGE_COLUMNS,
} from './concerns/HasBookmarksPriorityList';
import { type RecordId } from 'javascripts/shared-types';
import Project from './Project';

interface BookmarksIdeaPriorityList
  extends BookmarksIdeaPriorityListAttributes,
    HasBookmarksPriorityList {}

@model
@HasBookmarksPriorityList
class BookmarksIdeaPriorityList extends ApplicationModel {
  // Start auto-generated
  static typename: 'BookmarksIdeaPriorityList' = 'BookmarksIdeaPriorityList';
  get typename(): 'BookmarksIdeaPriorityList' {
    return 'BookmarksIdeaPriorityList';
  }

  static internalTypename: 'Bookmarks::IdeaPriorityList' =
    'Bookmarks::IdeaPriorityList';
  get internalTypename(): 'Bookmarks::IdeaPriorityList' {
    return 'Bookmarks::IdeaPriorityList';
  }

  static bookmarkName = 'Bookmarks::IdeaPriorityList';

  static inputType = 'PriorityListAttributes!';

  static fields = {
    advancedFilters: attr({ readable: true }),
    advancedFiltersParsed: attr({ readable: true, json: true }),
    clearFilters: attr({ writable: true, readable: false }),
    columnTables: attr({ readable: true, json: true }),
    filters: attr({ readable: true }),
    hasShippableRecords: attr({ readable: true }),
    id: attr({ readable: true }),
    joins: attr({ readable: true, json: true }),
    projectId: attr({ readable: true }),
    totalPrioritizedRecords: attr({ readable: true }),
    totalUnprioritizedRecords: attr({ readable: true }),
    userAllowedToPublishRanks: attr({ readable: true }),
    lastPublishedField: attr.belongsTo({
      readable: true,
      typename: 'CustomFieldDefinition',
    }),
    prioritizedRow: attr.belongsTo({ readable: true, typename: 'PivotRow' }),
    prioritizedRows: attr.belongsTo({
      args: { page: 'Int', per: 'Int' },
      readable: true,
      typename: 'PivotRowPage',
    }),
    project: attr.belongsTo({ readable: true, typename: 'Project' }),
    columnWidths: attr.hasMany({ writable: true, readable: true }),
    columns: attr.hasMany({ writable: true, readable: true }),
    lineBreaks: attr.hasMany({ writable: true, readable: true }),
    publishableCustomFields: attr.hasMany({ readable: true }),
    scoringSystemMetrics: attr.hasMany({ readable: true }),
    unprioritizedRecords: attr.hasMany({ readable: true }),
  };
  // End auto-generated

  static get updateFragmentName() {
    return 'updateBookmarksIdeaPriorityList';
  }

  static get bookmarkType() {
    return 'Bookmarks::IdeaPriorityList';
  }

  static get recordType() {
    return Idea;
  }

  // Ideas don't have a card layout so some buildScopes are different
  static buildScope() {
    return this.select(SHARED_BOOKMARK_COLUMNS).merge(
      SHARED_BOOKMARK_MERGE_COLUMNS
    );
  }

  static buildUnprioritizedRecordScope() {
    return Idea.select([
      'id',
      'path',
      'name',
      'score',
      'votes',
      'referenceNum',
      'projectId',
    ]).merge({
      permissions: ['update'],
      project: Project.select(['id', 'name']).merge({
        permissions: ['update'],
      }),
      workflowStatus: ['color', 'name'],
    });
  }

  get cardView() {
    return null;
  }

  get cardLayout() {
    return null;
  }

  static getWorkflowsForProjects(projectIds: RecordId[]) {
    return projectWorkflowScope(
      [Idea, Feature, Epic, Initiative, Release].map(model => model.typename)
    )
      .where({ id: projectIds })
      .all();
  }
}

export default BookmarksIdeaPriorityList;
